import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCmsPage, ImageBaseUrl } from '../../api/api';
import { useTranslation } from '../../hooks/useTranslation';
import Footer from '../../components/Footer';
import CustomAlert from '../../components/CustomAlert';
import { logoutUser } from '../../redux/userSlice';
import '../../css/LegalPage.css';

const LegalPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.user.lang) || 'EN';
  const user = useSelector((state) => state.user.user);
  const t = useTranslation();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    CustomAlert.confirm(
      t('Log_out') || 'Log out',
      t('are_you_sure_you_want_to_logout'),
      () => {
        dispatch(logoutUser());
        navigate('/login');
      },
      null,
      t('yes_confirm'),
      t('Cancel')
    );
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchCmsPage(slug, lang)
      .then((data) => {
        if (isMounted) {
          setPageData(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err?.response?.data?.message || 'Page not found');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug, lang]);

  const handleLogoClick = () => {
    if (user && user.status === 1) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const renderHeader = () => (
    <header className="cms-page-header-bar">
      <div className="ss_logo_hed_sec">
        <div className="ss_logo_lft_div">
          <img
            src="/img/RocketBPM_rocket_logo.png"
            alt="logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
        {user && user.status === 1 && (
          <div className="ss_profile_rit_div" style={{ flexDirection: 'row' }}>
            <div className="ss_header_new_right" ref={dropdownRef} style={{ position: 'relative' }}>
              <div id="dropdownBtn" onClick={toggleDropdown}>
                {user?.profile_image ? (
                  <>
                    <img
                      src={
                        user?.profile_image.startsWith('http')
                          ? user.profile_image
                          : `${ImageBaseUrl}uploads/profile_images/${user.profile_image}`
                      }
                      alt="Profile"
                      style={{ cursor: 'pointer' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextSibling;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                    <img
                      src="/img/user-circle-solid.svg"
                      alt=""
                      style={{ cursor: 'pointer', display: 'none' }}
                    />
                  </>
                ) : (
                  <img src="/img/user-circle-solid.svg" alt="" style={{ cursor: 'pointer' }} />
                )}
              </div>
              {dropdownOpen && (
                <div className="dropdown-content">
                  <button onClick={() => navigate('/account/settings')} className="dropdown-link">
                    {t('Edit_Profile')}
                  </button>
                  <button onClick={() => handleLogout()} className="dropdown-link">
                    {t('Log_out')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f4f6f9',
        }}
      >
        {renderHeader()}
        <div
          style={{
            flex: '1 0 auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 20px',
          }}
        >
          <div className="legal-page-wrapper loading">
            <div className="spinner"></div>
            <p>{t('loading') !== 'loading' ? t('loading') : 'Loading...'}</p>
          </div>
        </div>
        <div style={{ backgroundColor: '#ffffff', width: '100%' }}>
          <Footer />
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f4f6f9',
        }}
      >
        {renderHeader()}
        <div
          style={{
            flex: '1 0 auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 20px',
          }}
        >
          <div className="legal-page-wrapper error-state">
            <div className="error-icon">⚠️</div>
            <h2>
              {t('page_not_found') !== 'page_not_found' ? t('page_not_found') : 'Page Not Found'}
            </h2>
            <p>{error || 'The page you are looking for does not exist or has been moved.'}</p>
          </div>
        </div>
        <div style={{ backgroundColor: '#ffffff', width: '100%' }}>
          <Footer />
        </div>
      </div>
    );
  }

  // Format dynamic last updated date
  const formattedDate = pageData.updated_at
    ? new Date(pageData.updated_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#f4f6f9',
      }}
    >
      {renderHeader()}

      <div
        className="legal-page-container"
        style={{ flex: '1 0 auto', minHeight: 'auto', padding: '40px 20px' }}
      >
        <div className="legal-page-wrapper">
          <header className="legal-page-header">
            <h1>{pageData.title}</h1>
            {pageData.version && (
              <p className="legal-page-version">
                Version: {pageData.version} {formattedDate && `| Last Updated: ${formattedDate}`}
              </p>
            )}
          </header>
          <main
            className="legal-page-content"
            dangerouslySetInnerHTML={{ __html: pageData.content }}
          />
        </div>
      </div>

      <div style={{ backgroundColor: '#ffffff', width: '100%' }}>
        <Footer />
      </div>
    </div>
  );
};

export default LegalPage;
